import { TemplateRef } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

/**
 * Configuration interfaces for FieldRendererComponent.
 *
 * @example Text field with hyperlink
 * ```ts
 * const config: FieldConfig<MyRow> = {
 *   key: 'orderId',
 *   type: 'text',
 *   isHyperlink: true,
 *   hyperlinkCondition: (row) => row.status === 'active',
 *   displayFn: (row) => `#${row.orderId}`
 * };
 * ```
 *
 * @example Input with dynamic type
 * ```ts
 * const config: FieldConfig<MyRow> = {
 *   key: 'value',
 *   type: 'input',
 *   dynamicInputType: (row, form) =>
 *     form.get('type')?.value === 'decimal' ? 'decimal' : 'number',
 *   maxDigits: 10
 * };
 * ```
 *
 * @example Select with options
 * ```ts
 * const config: FieldConfig<MyRow, StatusOption> = {
 *   key: 'status',
 *   type: 'select',
 *   options: [{ id: 1, name: 'Active' }, { id: 2, name: 'Inactive' }],
 *   optionDisplayField: 'name',
 *   optionValueField: 'id',
 *   onSelectionChange: (event, rowIndex) => console.log(event)
 * };
 * ```
 *
 * @example Autocomplete with async search
 * ```ts
 * const config: FieldConfig<MyRow> = {
 *   key: 'customer',
 *   type: 'autocomplete',
 *   onSearch: (term) => customerService.search(term),
 *   initialOptions: [],
 *   minSearchLength: 2,
 *   debounceTime: 300
 * };
 * ```
 *
 * @example Conditional disabled state
 * ```ts
 * const config: FieldConfig<MyRow> = {
 *   key: 'quantity',
 *   type: 'input',
 *   inputType: 'number',
 *   disabledFn: (row, form) => row.isLocked || form?.get('status')?.value === 'closed'
 * };
 * ```
 */

/**
 * Supported field types
 */
export type FieldType =
  | 'text'
  | 'input'
  | 'select'
  | 'autocomplete'
  | 'time'
  | 'number'
  | 'multiselect';

export type InputSubType =
  | 'basic'
  | 'alphanumeric'
  | 'alphanumericHyphen'
  | 'decimal'
  | 'number'
  | 'postalCode'
  | 'numbersWithHyphen'
  | 'timeHhMm';

export interface OptionItem {
  id?: string | number;
  name?: string;
  [key: string]: unknown;
}

export interface FieldConfig<TRow = unknown, TOption = OptionItem> {
  key: string;
  type: FieldType;
  cssClass?: string;

  disabled?: boolean;

  displayFn?: (row: TRow) => string;

  isHyperlink?: boolean;
  hyperlinkCondition?: (row: TRow) => boolean;

  inputType?: InputSubType;

  trim?: boolean;
  uppercase?: boolean;
  disabledFn?: (row: TRow, formGroup?: AbstractControl) => boolean;
  /** When true, the field renders as read-only text even in edit/input-row mode. */
  renderAsTextFn?: (row: TRow, rowIndex: number) => boolean;
  maxLength?: number;
  maxDigits?: number;
  placeholder?: string;
  dynamicInputType?: (row: TRow, formGroup: AbstractControl) => InputSubType;

  options?: TOption[];
  getOptions?: (row: TRow, rowIndex: number) => TOption[] | undefined;
  optionDisplayField?: keyof TOption | string;
  optionValueField?: keyof TOption | string;
  multiple?: boolean;

  onSearch?: (searchTerm: string) => Observable<TOption[]>;
  initialOptions?: TOption[] | Observable<TOption[]>;
  minSearchLength?: number;
  debounceTime?: number;
  apiControlName?: string;

  editTemplate?: TemplateRef<FieldTemplateContext<TRow>>;

  errorMessages?: Record<string, string>;

  onValueChange?: (value: unknown, rowIndex: number) => void;
  onBlur?: (value: unknown, rowIndex: number) => void;
  onSelectionChange?: (event: unknown, rowIndex: number) => void;

  showInfoTooltip?: boolean;
  infoTooltipText?: string;
  infoTooltipPosition?: 'above' | 'below' | 'left' | 'right';
}

export interface FieldTemplateContext<TRow = unknown> {
  $implicit: AbstractControl | null;
  row: TRow;
  rowIndex: number;
  formGroup?: FormGroup;
}

export interface ColumnConfig<TRow = unknown, TOption = OptionItem> {
  key: keyof TRow | string;
  label: string;
  fieldConfig?: FieldConfig<TRow, TOption>;
  displayFn?: (row: TRow) => string;
  isHyperlink?: boolean;
  showRowShortcutIcon?: boolean;
  linkRoute?: string;
  hyperlinkCondition?: (row: TRow) => boolean;
  displayTemplate?: TemplateRef<DisplayTemplateContext<TRow>>;
  editTemplate?: TemplateRef<FieldTemplateContext<TRow>>;
  sortable?: boolean;
  defaultPrintWidth?: number;
  width?: number;
  isSelected?: boolean;
  defaultView?: boolean;
  showTooltip?: boolean;
  infoTooltipPosition?: 'above' | 'below' | 'left' | 'right';
  infoIconTooltipFn?: (row: TRow) => string;
  infoIconTooltipClass?: string;
  infoIconCssClass?: string;
  isActionButton?: boolean;
  actionButtonLabel?: string;
  /** Renders one or more row action buttons (gateway popup parity). */
  isRowActions?: boolean;
  rowActionsFn?: (row: TRow) => readonly string[];
  rowActionDisabledFn?: (row: TRow, action: string) => boolean;
  cellClassFn?: (row: TRow) => string;
}

export interface DisplayTemplateContext<TRow = unknown> {
  $implicit: TRow;
  value: string;
  rowIndex: number;
}

export interface FieldChangeEvent<TRow = unknown> {
  key: string;
  value: unknown;
  row: TRow;
  rowIndex: number;
}

export interface FieldClickEvent<TRow = unknown> {
  key: string;
  row: TRow;
  rowIndex: number;
}
